/**
 * Shared Journal CTA routing.
 *
 * One helper for every Journal call to action (desktop and mobile) so the
 * signed-out, signed-in and incomplete-onboarding journeys cannot diverge.
 *
 * - Signed out → consumer `/join` branch carrying `intent=journal`.
 * - Signed in, onboarding complete → canonical Journal route.
 * - Signed in, onboarding incomplete → consumer onboarding, intent preserved.
 */
import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccountCapabilities } from "@/hooks/useAccountCapabilities";
import {
  CONSUMER_JOIN_JOURNAL_ROUTE,
  JOURNAL_ROUTE,
} from "@/lib/homepage-ctas";
import { persistReturnIntent, withReturnIntent } from "@/lib/return-intent";

export interface JournalCta {
  /** Locale-relative destination for the CTA. */
  to: string;
  /** Call from the CTA's onClick so the intent survives auth callbacks. */
  onClick: () => void;
  /** True while auth/capability state is still resolving. */
  isResolving: boolean;
}

export function useJournalCta(): JournalCta {
  const { user, loading } = useAuth();
  const { capabilities, isLoading } = useAccountCapabilities();

  const isResolving = loading || isLoading;

  let to = CONSUMER_JOIN_JOURNAL_ROUTE;
  if (user) {
    to = capabilities.consumerOnboardingComplete
      ? JOURNAL_ROUTE
      : withReturnIntent("/app/onboarding", "journal");
  }

  const onClick = useCallback(() => {
    // Persisted for every state: an authenticated member with incomplete
    // onboarding still needs the intent restored at completion.
    persistReturnIntent("journal");
  }, []);

  return { to, onClick, isResolving };
}
